// Product data — single source of truth for the DUNC collection.
// Ingredients & nutrition are supplied by DUNC and shared across all three flavours
// except the flavour-specific syrup.

const BASE_INGREDIENTS = [
  "Live Kombucha Culture (Yeast & Bacteria Cultures)",
  "Organic Green Tea",
  "Organic Black Tea",
  "Thyme",
  "Rosemary",
  "Peppermint",
  "Basil",
  "{SYRUP}",
  "Lemongrass",
  "Chamomile",
  "Oregano",
  "Clove",
  "Nutmeg",
  "Cinnamon",
  "Fennel",
  "Cardamom",
  "Black Cardamom",
  "Stevia",
  "Cane Sugar",
  "Filtered Water",
  "100% Pure Love",
];

const withSyrup = (syrup) =>
  BASE_INGREDIENTS.map((i) => (i === "{SYRUP}" ? syrup : i));

// Both formats are available in every flavour.
const FORMATS = [
  {
    type: "can",
    volume: "250 ML",
    note: "Grab. Chill. Go.",
    blurb: "The everyday DUNC. Ice-cold, crackable, made for movement.",
  },
  {
    type: "bottle",
    volume: "330 ML",
    note: "Slow down. Pour. Discover.",
    blurb: "The ritual DUNC. Made to be poured, watched, and savoured.",
  },
];

export const NUTRITION = {
  serving: "330 ml",
  perServing: [
    { name: "Energy",       value: "77.9 kcal", rda: "3.9%" },
    { name: "Protein",      value: "0 g",       rda: "0%" },
    { name: "Carbohydrate", value: "19.5 g",    rda: "—" },
    { name: "Total Sugars", value: "4.52 g",    rda: "—" },
    { name: "Added Sugars", value: "4.52 g",    rda: "9%" },
    { name: "Total Fat",    value: "0 g",       rda: "0%" },
    { name: "Sodium",       value: "33 mg",     rda: "1.7%" },
    { name: "Vitamin C",    value: "3.96 mg",   rda: "5%" },
    { name: "Calcium",      value: "59.4 mg",   rda: "6%" },
    { name: "Iron",         value: "3.96 mg",   rda: "19%" },
    { name: "Manganese",    value: "0.79 mg",   rda: "—" },
  ],
  note: "% RDA values are based on 2000 kcal diet as per ICMR Guidelines for a moderate work adult. Values marked '—' are not established.",
};

export const PRODUCTS = [
  {
    slug: "mint-mojito",
    name: "Mint Mojito",
    volume: "330 ML",
    tagline: "Fresh. Bright. Cooling.",
    accent: "#8CC63F",           // label green
    accentSoft: "rgba(140,198,63,0.14)",
    surface: "#1E2617",          // muted deep green for section bg
    tasting:
      "Crisp organic tea meets peppermint and basil, lifted with a soft citrus edge and a whisper of warm spice. Clean, sparkling, made to refresh.",
    notes: ["Peppermint", "Basil", "Lemongrass", "Fresh citrus lift"],
    ingredients: withSyrup("Mint Mojito Syrup"),
    story:
      "Our reset button. Slow-fermented organic green and black teas meet a garden of peppermint, basil and lemongrass, lifted with a bright citrus edge. Brewed in small batches, never rushed — the kind of cold, green clarity that makes you put the phone down for a minute.",
    serving:
      "Serve ice-cold, straight from the can — or pour over crushed ice with a bruised mint sprig and a lime wheel. Best at 4–6°C.",
    image: {
      hero: "/products/mint-mojito-1200.webp",
      card: "/products/mint-mojito-800.webp",
      small: "/products/mint-mojito-500.webp",
      srcSet: "/products/mint-mojito-500.webp 500w, /products/mint-mojito-800.webp 800w, /products/mint-mojito-1200.webp 1200w",
      sizes: "(max-width: 768px) 90vw, 40vw",
    },
    canImage: {
      card: "/products/mint-mojito-can-800.webp",
      small: "/products/mint-mojito-can-500.webp",
      srcSet: "/products/mint-mojito-can-500.webp 500w, /products/mint-mojito-can-800.webp 800w",
      sizes: "(max-width: 768px) 90vw, 40vw",
    },
    formats: FORMATS,
    order: 1,
    status: "launching",
  },
  {
    slug: "passion-fruit",
    name: "Passion Fruit",
    volume: "330 ML",
    tagline: "Tropical. Vibrant. Alive.",
    accent: "#F0A040",           // label orange
    accentSoft: "rgba(240,160,64,0.14)",
    surface: "#2A1E12",
    tasting:
      "Tropical passion fruit tang layered over floral herbs and warm baking spices. Finishes dry, lively and slightly effervescent.",
    notes: ["Passion fruit", "Chamomile", "Cardamom", "Warm baking spices"],
    ingredients: withSyrup("Passion Fruit Syrup"),
    story:
      "The loud one, in the best way. Ripe passion fruit tang layered over chamomile, cardamom and warm baking spice, all carried on our slow-fermented tea base. It finishes dry and alive — tropical without ever turning sugary.",
    serving:
      "Chill hard, crack, and pour into a highball over ice. A basil leaf or a slit vanilla pod turns it into an occasion. Best at 4–6°C.",
    image: {
      hero: "/products/passion-fruit-1200.webp",
      card: "/products/passion-fruit-800.webp",
      small: "/products/passion-fruit-500.webp",
      srcSet: "/products/passion-fruit-500.webp 500w, /products/passion-fruit-800.webp 800w, /products/passion-fruit-1200.webp 1200w",
      sizes: "(max-width: 768px) 90vw, 40vw",
    },
    canImage: {
      card: "/products/passion-fruit-can-800.webp",
      small: "/products/passion-fruit-can-500.webp",
      srcSet: "/products/passion-fruit-can-500.webp 500w, /products/passion-fruit-can-800.webp 800w",
      sizes: "(max-width: 768px) 90vw, 40vw",
    },
    formats: FORMATS,
    order: 2,
    status: "launching",
  },
  {
    slug: "spiced-jamun",
    name: "Spiced Jamun",
    volume: "330 ML",
    tagline: "Deep. Fruity. Distinctly Indian.",
    accent: "#B569D0",           // label purple
    accentSoft: "rgba(181,105,208,0.14)",
    surface: "#231428",
    tasting:
      "Ripe jamun meets clove, cinnamon and cardamom over a slow-fermented tea base. Bold, dry, and unmistakably Indian.",
    notes: ["Jamun", "Clove", "Cinnamon", "Black Cardamom"],
    ingredients: withSyrup("Spiced Jamun Syrup"),
    story:
      "The one we're proudest of. Ripe jamun — monsoon-dark, faintly wild — folded into clove, cinnamon and black cardamom over a slow-fermented tea base. Bold, dry and unmistakably Indian. A flavour your grandmother would recognise, dressed for tonight.",
    serving:
      "Pour slowly into a stemmed glass and let the spice bloom for a few seconds before the first sip. Pairs beautifully with rich, fried and festive food. Best at 4–8°C.",
    image: {
      hero: "/products/spiced-jamun-mood-1200.webp",
      card: "/products/spiced-jamun-800.webp",
      small: "/products/spiced-jamun-500.webp",
      splash: "/products/spiced-jamun-splash-1200.webp",
      srcSet: "/products/spiced-jamun-500.webp 500w, /products/spiced-jamun-800.webp 800w",
      sizes: "(max-width: 768px) 90vw, 40vw",
    },
    canImage: {
      card: "/products/spiced-jamun-can-800.webp",
      small: "/products/spiced-jamun-can-500.webp",
      srcSet: "/products/spiced-jamun-can-500.webp 500w, /products/spiced-jamun-can-800.webp 800w",
      sizes: "(max-width: 768px) 90vw, 40vw",
    },
    formats: FORMATS,
    order: 3,
    status: "launching",
  },
];

// Hero triptych — 250 ML cans lead the site.
export const HERO_TRIPTYCH = [
  {
    src: "/products/mint-mojito-can-800.webp",
    srcSet: "/products/mint-mojito-can-500.webp 500w, /products/mint-mojito-can-800.webp 800w",
    alt: "DUNC Mint Mojito sparkling kombucha — 250 ml can",
    tint: "#8CC63F",
  },
  {
    src: "/products/passion-fruit-can-800.webp",
    srcSet: "/products/passion-fruit-can-500.webp 500w, /products/passion-fruit-can-800.webp 800w",
    alt: "DUNC Passion Fruit sparkling kombucha — 250 ml can",
    tint: "#F0A040",
  },
  {
    src: "/products/spiced-jamun-can-800.webp",
    srcSet: "/products/spiced-jamun-can-500.webp 500w, /products/spiced-jamun-can-800.webp 800w",
    alt: "DUNC Spiced Jamun sparkling kombucha — 250 ml can",
    tint: "#B569D0",
  },
];
